import { HintAnimation } from '../hint-motion';
import { hline, text } from '../../academy/films/story-scene';
import { abs, C, glass, icon, orbiTemplate } from './scene-kit';
import { CONTENT, HintStory, portalShell } from './hint-story';

/**
 * Hinweis D „Teste deinen KI-Agenten“.
 *
 * Didaktik: Vertrauen entsteht, wenn man selbst sieht, dass der Agent aus
 * den EIGENEN Unterlagen antwortet. WARUM „Teste deinen Agenten — bevor
 * Lernende ihn sehen“ → WO Play-Knopf auf der Agent-Kachel („Chat-Vorschau
 * starten“) → WAS eine typische Lernendenfrage, die Antwort entsteht →
 * GESCHAFFT die Quelle aus der eigenen Wissensbasis leuchtet auf, Orbi lacht.
 * So wird klar, worauf man beim Testen achten soll: auf die Quelle.
 */

const CHAT = { x: 404, y: 70, w: 216, h: 262 };
const PLAY: [number, number] = [CONTENT.x + 126, CONTENT.y + 100];
const INPUT: [number, number] = [CHAT.x + 100, CHAT.y + 237];
const PRIMARY = '#003598';
const SECONDARY = '#3B82F6';

const tile = (i: number, name: string, play: boolean) =>
  glass(
    18,
    0.55,
    `left:${20 + i * 164}px;top:64px;width:150px;height:120px;`,
    `<div style="${abs(57, 12, 36, 36)}">${orbiTemplate(`t${i}`, 36)}</div>` +
      text(8, 58, 134, name, 12.5, 600, C.text, 'text-align:center;') +
      (play
        ? `<div data-r="playBtn" style="${abs(61, 84, 28, 28, `border-radius:999px;background:${C.action};display:flex;align-items:center;justify-content:center;box-shadow:0 6px 12px -6px rgb(37 99 235 / .8);`)}">${icon('play', 14, '#fff', 2)}</div>`
        : ''),
  );

const content = text(20, 12, 300, 'KI-Agenten', 18, 400) + hline(16, 46, 428) + tile(0, 'Statistik-Tutor', true) + tile(1, 'Mathe-Tutor', false);

const tip =
  `<div data-r="tip" style="${abs(PLAY[0] - 70, PLAY[1] - 44, 140, 24, 'opacity:0;border-radius:6px;background:#243650;color:#fff;font-size:11px;font-weight:500;display:flex;align-items:center;justify-content:center;white-space:nowrap;')}">Chat-Vorschau starten</div>`;

const botBubble = (x: number, y: number, w: number, inner: string, ref = '') =>
  `<div ${ref ? `data-r="${ref}"` : ''} style="${abs(x, y, w, 0, `height:auto;padding:8px 11px;border-radius:14px;border-bottom-left-radius:4px;background:${SECONDARY};color:#fff;font-size:11.5px;line-height:1.4;box-sizing:border-box;${ref ? 'opacity:0;' : ''}`)}">${inner}</div>`;

const chat =
  `<div data-r="chat" style="opacity:0;">` +
  `<div style="${abs(CHAT.x, CHAT.y, CHAT.w, CHAT.h, 'border-radius:16px;overflow:hidden;background:#fff;box-shadow:0 18px 36px -16px rgb(20 38 70 / .45), 0 0 0 1px rgb(83 109 145 / .12);')}">` +
  `<div style="${abs(0, 0, CHAT.w, 40, `background:${PRIMARY};display:flex;align-items:center;gap:8px;padding:0 12px;box-sizing:border-box;`)}">` +
  `<div style="width:22px;height:22px;position:relative;">${orbiTemplate('ch', 22)}</div>` +
  `<div style="font-size:12.5px;font-weight:600;color:#fff;">Statistik-Tutor</div></div>` +
  botBubble(12, 52, 170, 'Hallo! Was möchtest du lernen?') +
  `<div data-r="q" style="${abs(76, 100, 128, 0, `opacity:0;height:auto;padding:8px 11px;border-radius:14px;border-bottom-right-radius:4px;background:#fff;color:#1f2937;font-size:11.5px;line-height:1.4;box-sizing:border-box;box-shadow:0 0 0 1px rgb(83 109 145 / .2);`)}">Was ist ein Median?</div>` +
  `<div data-r="ansBox" style="opacity:0;">${botBubble(12, 142, 182, '<span data-r="ans"></span>')}</div>` +
  `<div data-r="src" style="${abs(12, 200, 168, 22, `opacity:0;border-radius:999px;background:rgb(59 130 246 / .1);display:flex;align-items:center;gap:6px;padding:0 10px;box-sizing:border-box;font-size:10.5px;font-weight:600;color:${C.action};white-space:nowrap;`)}">${icon('file', 12, C.action, 2)}Skript Statistik, S. 12</div>` +
  `<div data-r="srcGlow" style="${abs(12, 200, 168, 22, 'border-radius:999px;')}"></div>` +
  `<div style="${abs(10, 226, 196, 26, 'border-radius:999px;background:#f3f4f6;')}">` +
  `<div data-r="typed" style="${abs(12, 0, 170, 26, 'display:flex;align-items:center;font-size:11.5px;color:#1f2937;white-space:pre;')}"></div></div>` +
  `</div></div>`;

export const tryAgentAnimation = (): HintAnimation =>
  new HintStory({
    intro: { line1: ['Teste', 'deinen', 'Agenten'], line2: ['bevor', 'Lernende', 'ihn', 'sehen'] },
    portal: portalShell({ active: 0, content }) + tip + chat,
    tracks: [
      { r: 'tip', at: [1.3, 1.4, 1.6, 1.68], o: [0, 1, 1, 0] },
      { r: 'chat', at: [1.68, 1.92], o: [0, 1], y: [16, 0] },
      { r: 'typed', at: [2.4, 2.44], o: [1, 0] },
      { r: 'q', at: [2.42, 2.58], o: [0, 1], y: [6, 0] },
      { r: 'ansBox', at: [2.66, 2.76], o: [0, 1] },
      { r: 'src', at: [3.34, 3.5], o: [0, 1], y: [4, 0] },
    ],
    typing: [
      { r: 'typed', text: 'Was ist ein Median?', from: 2.02, to: 2.36 },
      { r: 'ans', text: 'Der Median ist der mittlere Wert einer sortierten Reihe.', from: 2.72, to: 3.28 },
    ],
    glows: [
      { r: 'playBtn', at: 1.15, dur: 0.45 },
      { r: 'srcGlow', at: 3.5, dur: 0.8 },
    ],
    orbis: [
      { prefix: 'ch', pop: 2.7, laugh: 3.55 },
      { prefix: 't0', pop: 1.62, laugh: 99 },
    ],
    cursor: {
      at: [0, 1.1, 1.52, 1.64, 1.9, 2.0, 2.5, 99],
      x: [300, 300, PLAY[0], PLAY[0], INPUT[0], INPUT[0], 380, 380],
      y: [250, 250, PLAY[1], PLAY[1], INPUT[1], INPUT[1], 320, 320],
      clicks: [1.6, 1.95],
    },
    camera: {
      at: [0, 1.75, 2.0, 3.95, 4.25, 99],
      x: [320, 320, 398, 398, 320, 320],
      y: [176, 176, 202, 202, 176, 176],
      s: [1, 1, 1.32, 1.32, 1, 1],
    },
    still: 3.95,
  });
